import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DataGrid, KpiCard, Spinner,
  type DataGridColumn,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  ChevronRight, FileBarChart, Pencil, Play, Plus, RefreshCw, Sparkles, Wand2,
} from 'lucide-react';

/**
 * Saved-reports list. KPIs the way an analytics-lead reads them:
 *   total reports · reports last 30 days · scheduled · ad-hoc.
 */

interface ReportRow extends Record<string, unknown> {
  id?: number | string;
  reportDefinitionId?: string;
  name?: string;
  code?: string;
  description?: string;
  sourceResource?: string;
  sourceGroup?: string;
  config?: string | object;
  isActive?: number | boolean;
  createdAt?: string;
  updatedAt?: string;
}

export default function ReportDefinitionList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const r = await apiClient.get('/reporting/ReportDefinition');
      const list = pickList<ReportRow>(r.data);
      // The Group/Resource columns are derived from the persisted spec
      // (layoutJson / queryDefinition / legacy config) — there are no
      // sourceGroup/sourceResource columns on the backend entity. Parse once
      // here so those columns fill instead of always rendering blank.
      const enriched = list.map((row) => {
        const spec = parseSpec(row);
        return {
          ...row,
          sourceGroup: row.sourceGroup ?? spec?.group ?? (row.category != null ? String(row.category) : undefined),
          sourceResource: row.sourceResource ?? spec?.resource,
        };
      });
      setRows(enriched);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const kpis = useMemo(() => {
    const total = rows.length;
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recent = rows.filter((r) => {
      const t = Date.parse(String(r.updatedAt ?? r.createdAt ?? ''));
      return !Number.isNaN(t) && t >= cutoff;
    }).length;
    const groups = new Set(rows.map((r) => String(r.sourceGroup ?? '')).filter(Boolean));
    const active = rows.filter((r) => !!r.isActive).length;
    return { total, recent, groups: groups.size, active };
  }, [rows]);

  const columns = useMemo<DataGridColumn<ReportRow>[]>(
    () => [
      { accessor: 'name', title: 'Report', sortable: true, filterable: true,
        render: (r) => (
          <Link
            to={`/panel/reports/${encodeURIComponent(String(r.reportDefinitionId ?? r.id ?? ''))}`}
            className="font-medium text-primary hover:underline"
          >
            {String(r.name ?? r.code ?? r.id ?? '—')}
          </Link>
        )
      },
      { accessor: 'code', title: 'Code', sortable: true, filterable: true },
      { accessor: 'sourceGroup', title: 'Group', sortable: true, filterable: true },
      { accessor: 'sourceResource', title: 'Resource', sortable: true, filterable: true },
      { accessor: 'description', title: 'Description',
        render: (r) => (
          <span className="line-clamp-1 max-w-[360px] text-content-secondary">
            {String(r.description ?? '—')}
          </span>
        )
      },
      { accessor: 'updatedAt', title: 'Updated', sortable: true,
        render: (r) => fmtDate(r.updatedAt ?? r.createdAt) },
      {
        accessor: '__actions' as never, title: '', width: 110,
        render: (r) => {
          const id = String(r.reportDefinitionId ?? r.id ?? '');
          return (
            <div className="flex items-center justify-end gap-2">
              <Link
                to={`/panel/reports/${encodeURIComponent(id)}`}
                title="Edit"
                className="text-content-muted hover:text-primary"
              >
                <Pencil size={14} />
              </Link>
              <Link
                to={`/panel/reports/${encodeURIComponent(id)}#run`}
                title="Run"
                className="text-content-muted hover:text-success"
              >
                <Play size={14} />
              </Link>
              <Link
                to={`/panel/reports/${encodeURIComponent(id)}`}
                className="text-content-muted hover:text-primary"
              >
                <ChevronRight size={16} />
              </Link>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Reports</h1>
          <p className="text-xs text-content-muted">
            Saved report definitions · live data from /api/reporting/ReportDefinition
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate('/panel/reports/party-wise')}>
          Party-wise
        </Button>
        <Button size="sm" onClick={() => navigate('/panel/reports/new')}>
          <Plus className="h-3.5 w-3.5" /> New report
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Saved reports"
          value={kpis.total.toLocaleString()}
          subtitle="All definitions"
          icon={<FileBarChart className="h-5 w-5 text-primary" />}
        />
        <KpiCard
          title="Active"
          value={kpis.active.toLocaleString()}
          subtitle="Available to run"
          icon={<Play className="h-5 w-5 text-success" />}
        />
        <KpiCard
          title="Edited (30d)"
          value={kpis.recent.toLocaleString()}
          subtitle="Last 30 days"
          icon={<Wand2 className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Modules covered"
          value={kpis.groups.toLocaleString()}
          subtitle="Distinct API groups"
          icon={<Sparkles className="h-5 w-5 text-warning" />}
        />
      </div>

      {loading && rows.length === 0 ? (
        <Card>
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
            <Spinner /> Loading reports…
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <div className="p-6 text-sm text-content-muted">
            No reports yet. Click <em>New report</em> to compose your first one — pick any of the
            287 live-API resources, choose columns, add filters, and save.
          </div>
        </Card>
      ) : (
        <DataGrid<ReportRow>
          data={rows}
          columns={columns}
          rowId={(rows[0]?.reportDefinitionId != null ? 'reportDefinitionId' : 'id') as keyof ReportRow & string}
        />
      )}
    </div>
  );
}

/** Parse the report spec from whichever real column holds it. */
function parseSpec(row: ReportRow): { resource?: string; group?: string } | null {
  for (const raw of [(row as Record<string, unknown>).layoutJson, (row as Record<string, unknown>).queryDefinition, row.config]) {
    if (typeof raw === 'string' && raw.trim()) {
      try { return JSON.parse(raw) as { resource?: string; group?: string }; } catch { /* try next */ }
    } else if (raw && typeof raw === 'object') {
      return raw as { resource?: string; group?: string };
    }
  }
  return null;
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function pickList<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}
